// A modal dialog.
//
// Hand-built rather than pulled from a component library, because the four
// behaviours that make a modal a modal are the whole of it: focus moves in when
// it opens and back where it came from when it closes, Tab cycles inside it,
// Escape dismisses it, and the surface is labelled by its own title so a screen
// reader announces what just opened.
//
// An `alertdialog` (the delete confirmations) deliberately does NOT close on a
// backdrop click. A stray click outside a form is a cancelled edit, which is
// recoverable; on a destructive confirmation the same click should not be able
// to dismiss the question the user is being asked.

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./Dialog.module.css";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** `alert` for destructive confirmations — see the note above. */
  variant?: "form" | "alert";
  narrow?: boolean;
  children: ReactNode;
  /** The button row. Rendered in a footer below a divider. */
  footer: ReactNode;
  /** Wraps the body in a `<form>`, so Enter submits. */
  onSubmit?: () => void;
}

export function Dialog({
  open,
  onClose,
  title,
  variant = "form",
  narrow = false,
  children,
  footer,
  onSubmit,
}: DialogProps): ReactNode {
  const surface = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement;

    // The first control, not the surface: a form should open with the cursor in
    // its first field rather than making the user Tab into it.
    const first = surface.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? surface.current)?.focus();

    // A background that scrolls under an open modal is disorienting, and on a
    // long form it hides the very thing being edited.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      if (restoreTo.current instanceof HTMLElement) restoreTo.current.focus();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        surface.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter((element) => element.offsetParent !== null || element === document.activeElement);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      // Wrap at both ends, which is what keeps Tab from walking out of the
      // dialog and into a page the user cannot see.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  const body = (
    <>
      <div className={styles.body}>{children}</div>
      <div className={styles.footer}>{footer}</div>
    </>
  );

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (variant === "alert") return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={surface}
        role={variant === "alert" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${styles.surface} ${narrow ? styles.narrow : ""}`}
        onKeyDown={onKeyDown}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
        </div>
        {onSubmit === undefined ? (
          body
        ) : (
          <form
            className={styles.formContents}
            // The browser's own constraint validation is off on purpose: it
            // would silently BLOCK submission and show a native bubble whose
            // wording varies by browser, instead of the message the shared
            // schema wrote. `required` still marks the field for assistive
            // tech; what a field must contain is the contract's decision.
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
